'use client';

import React, { useEffect, useState } from 'react';
import { Typography } from '@/components/atoms/Typography/Typography';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { ICommonProps } from '@/types/global';
import { Button } from '@/components/atoms/Button/Button';
import { Modal } from '@/components/atoms/Modal/Modal';
import { events } from '@/utils/events';
import { useTranslations } from 'next-intl';
import { getCookie, setCookie } from '@/utils/cookies';
import { allowPageScroll, blockPageScroll } from '@/utils/storeSetters';
import * as Styled from './CookieBannerModal.styled';

export interface ICookieBannerModal extends ICommonProps {
  scrollFromTop: number;
}

/** Displays a modal to accept or reject cookies
 * @param {string} scrollFromTop
 * @returns {JSX.Element}
 */
export const CookieBannerModal: React.FC<ICookieBannerModal> = ({
  scrollFromTop = 500,
}) => {
  const [displayModal, setDisplayModal] = useState(false);
  const t = useTranslations('cookies');
  const linkHTML = `<a href="${t('linkSlug')}">${t('linkText')}</a>`;
  const textCookies = t('text')
    .replace('--link--', linkHTML)
    .replaceAll('--br--', '<br/>');


  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (displayModal) {
      blockPageScroll();
    } else {
      allowPageScroll();
    }

  }, [displayModal]);

  useEffect(() => {
    const isBannerAlreadySubmited = () => {
      if (typeof window !== 'undefined') {
        const cookieValue = getCookie('cookies-accepted');
        return cookieValue === 'true' || cookieValue === 'false';
      }
      return false;
    };

    if (isBannerAlreadySubmited()) return;

    const handleScroll = () => {
      if (window.scrollY >= scrollFromTop) {
        setDisplayModal(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollFromTop]);

  useEffect(() => {
    if (getCookie('cookies-accepted') === 'true' && typeof window?.gtag === 'function') {

      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    }
  }, []);

  const onCookiesAccepted = () => {
    if (typeof window !== 'undefined') {
      setCookie('cookies-accepted', 'true');
      setDisplayModal(false);

      if (typeof window?.gtag === 'undefined') return;

      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    }
  };

  const onCookiesRejected = () => {
    if (typeof window !== 'undefined') {
      setCookie('cookies-accepted', 'false');
      setDisplayModal(false);

      if (typeof window?.gtag === 'undefined') return;

      window.gtag?.('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
  };

  return (
    <Modal
      toggleModalEventName={events.cookiesDisplayModal}
      displayModal={displayModal}
      positionContent="bottom"
      closeOnOutsideClick={false}
    >
      <ComponentContainer width={'main'} marginBottom="sm">
        <Styled.CookieBannerContainer>
          <Styled.TextContainer>
            <Typography text={t('title')} className="cookies-title" />
            <Typography text={textCookies} className="cookies-content" />
          </Styled.TextContainer>
          <Styled.ButtonsContainer>
            <Button
              text={t('acceptBtn')}
              className="btn-accept"
              callbackOnClick={onCookiesAccepted}
            />
            <Button
              text={t('rejectBtn')}
              className="btn-reject"
              callbackOnClick={onCookiesRejected}
            />
          </Styled.ButtonsContainer>
        </Styled.CookieBannerContainer>
      </ComponentContainer>
    </Modal>
  );
};
